/**
 * Unified Notification Service
 * Combines OneSignal + Native Web Push with fallbacks and retries
 */

import { supabase } from '@/integrations/supabase/client';

// ============= TYPES =============

export interface NotificationStatus {
  isSupported: boolean;
  permission: NotificationPermission;
  isIOS: boolean;
  isIOSSupportsPush: boolean;
  isInstalledPWA: boolean;
  hasNativeSubscription: boolean;
  hasDbSubscription: boolean;
  isFullyEnabled: boolean;
  subscriptionAge: number | null; // Age in days
  serviceWorkerState: 'active' | 'installing' | 'waiting' | 'none' | 'error';
}

export interface NotificationResult {
  success: boolean;
  method: 'onesignal' | 'native' | 'both' | 'none';
  error?: string;
}

// ============= CONSTANTS =============

const VAPID_PUBLIC_KEY = 'BP90LFmRD9u3bb-DGAEvfsXqWThDl9jgZIdaTOnB259IJVHRFuJ2UEVLFODdF_q5j1H5PDcH5yzdt60O9WFagnA';
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

// ============= PLATFORM DETECTION =============

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isInstalledPWA(): boolean {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return isStandalone || isIOSStandalone;
}

export function isIOSSupportsPush(): boolean {
  if (!isIOS()) return true;
  
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (match) {
    const major = parseInt(match[1], 10);
    const minor = parseInt(match[2], 10);
    if (major < 16 || (major === 16 && minor < 4)) {
      return false;
    }
  }
  return 'PushManager' in window;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

export function getPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

// ============= HELPERS =============

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============= NATIVE WEB PUSH =============

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  
  try {
    return await navigator.serviceWorker.ready;
  } catch (error) {
    console.error('[NotifService] SW registration error:', error);
    return null;
  }
}

async function getNativeSubscription(): Promise<PushSubscription | null> {
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Wait for service worker to be active with retries
 * iOS needs longer timeouts and may need SW to be explicitly registered
 */
async function waitForActiveServiceWorker(maxWaitMs = 10000): Promise<ServiceWorkerRegistration | null> {
  const startTime = Date.now();
  console.log('[NotifService] Waiting for SW to be active (max ' + maxWaitMs + 'ms)...');
  
  // First, check if SW is already registered
  let registration = await navigator.serviceWorker.getRegistration();
  
  // If no registration exists on iOS, try to register it
  if (!registration && isIOS()) {
    console.log('[NotifService] No SW registration found on iOS, registering...');
    try {
      registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      console.log('[NotifService] SW registered on iOS');
    } catch (regError) {
      console.error('[NotifService] SW registration failed on iOS:', regError);
    }
  }
  
  while (Date.now() - startTime < maxWaitMs) {
    try {
      // Use ready promise which resolves when SW is activated
      const readyRegistration = await Promise.race([
        navigator.serviceWorker.ready,
        delay(2000).then(() => null) // Timeout for each attempt
      ]);
      
      if (readyRegistration && readyRegistration.active) {
        const elapsed = Date.now() - startTime;
        console.log('[NotifService] ✓ SW is active (took ' + elapsed + 'ms)');
        return readyRegistration;
      }
      
      // Check current state
      const currentReg = await navigator.serviceWorker.getRegistration();
      if (currentReg) {
        const state = currentReg.active ? 'active' : 
                     currentReg.installing ? 'installing' : 
                     currentReg.waiting ? 'waiting' : 'none';
        console.log('[NotifService] SW state:', state);
        
        // If installing or waiting, wait for it to activate
        if (currentReg.installing) {
          console.log('[NotifService] SW is installing, waiting for activation...');
          await new Promise<void>((resolve) => {
            currentReg.installing!.addEventListener('statechange', function handler() {
              if (this.state === 'activated') {
                currentReg.installing?.removeEventListener('statechange', handler);
                resolve();
              }
            });
            // Also set a timeout
            setTimeout(resolve, 3000);
          });
        }
      }
      
      await delay(1000);
    } catch (error) {
      console.error('[NotifService] Error waiting for SW:', error);
      await delay(1000);
    }
  }
  
  console.error('[NotifService] ❌ SW did not become active in ' + maxWaitMs + 'ms');
  return null;
}

/**
 * Create native push subscription with detailed error handling
 */
async function createNativeSubscription(): Promise<PushSubscription | null> {
  console.log('[NotifService] Creating native subscription...');
  
  // Step 1: Wait for SW to be active
  console.log('[NotifService] Step 1: Waiting for active SW...');
  const registration = await waitForActiveServiceWorker();
  if (!registration) {
    console.error('[NotifService] ❌ Service Worker not ready');
    throw new Error('SERVICE_WORKER_NOT_READY');
  }
  console.log('[NotifService] ✓ SW ready');

  // Step 2: Check if PushManager exists
  console.log('[NotifService] Step 2: Checking PushManager...');
  if (!registration.pushManager) {
    console.error('[NotifService] ❌ PushManager not available');
    throw new Error('PUSH_MANAGER_NOT_AVAILABLE');
  }
  console.log('[NotifService] ✓ PushManager available');

  // Step 3: Subscribe with VAPID key
  console.log('[NotifService] Step 3: Subscribing with VAPID key...');
  try {
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey.buffer as ArrayBuffer
    });
    
    console.log('[NotifService] ✓ Native subscription created');
    console.log('[NotifService] Endpoint:', subscription.endpoint.substring(0, 80) + '...');
    return subscription;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[NotifService] ❌ pushManager.subscribe failed:', errorMessage);
    
    // Classify the error for better UX
    if (errorMessage.includes('denied') || errorMessage.includes('permission')) {
      throw new Error('PERMISSION_DENIED');
    } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      throw new Error('NETWORK_ERROR');
    } else {
      throw new Error('SUBSCRIPTION_FAILED: ' + errorMessage);
    }
  }
}

async function saveSubscriptionToDb(userId: string, subscription: PushSubscription): Promise<boolean> {
  const subscriptionJSON = subscription.toJSON();
  const keys = subscriptionJSON.keys;
  
  if (!keys?.p256dh || !keys?.auth) {
    console.error('[NotifService] Missing subscription keys');
    return false;
  }

  console.log('[NotifService] Saving subscription to DB (upsert by endpoint)...');

  // UPSERT: Update if endpoint exists for this user, insert otherwise
  // This allows multiple devices per user (different endpoints)
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({
      user_id: userId,
      endpoint: subscription.endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'user_id,endpoint'
    });

  if (error) {
    console.error('[NotifService] DB save error:', error);
    return false;
  }

  console.log('[NotifService] Subscription saved to DB');
  return true;
}

async function removeSubscriptionFromDb(userId: string, endpoint?: string): Promise<void> {
  if (endpoint) {
    // Remove only the subscription for this specific device
    console.log('[NotifService] Removing subscription for endpoint:', endpoint.substring(0, 50) + '...');
    await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId)
      .eq('endpoint', endpoint);
  } else {
    // Fallback: remove all subscriptions for this user
    console.log('[NotifService] Removing all subscriptions for user:', userId);
    await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId);
  }
}

// ============= MAIN API =============

/**
 * Request notification permission from browser
 */
export async function requestPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('[NotifService] Notifications not supported');
    return false;
  }

  try {
    console.log('[NotifService] Requesting permission...');
    const result = await Notification.requestPermission();
    console.log('[NotifService] Permission result:', result);
    return result === 'granted';
  } catch (error) {
    console.error('[NotifService] Permission error:', error);
    return false;
  }
}

/**
 * Subscribe user to push notifications (with retries)
 * Tries native Web Push + stores in DB for server-side sending
 */
export interface SubscribeResult {
  success: boolean;
  error?: string;
  errorCode?: 'SERVICE_WORKER_NOT_READY' | 'PUSH_MANAGER_NOT_AVAILABLE' | 'PERMISSION_DENIED' | 'NETWORK_ERROR' | 'SUBSCRIPTION_FAILED' | 'IOS_NOT_PWA' | 'IOS_OLD_VERSION' | 'NOT_SUPPORTED' | 'DB_SAVE_FAILED';
}

export async function subscribe(userId: string, retryCount = 0): Promise<SubscribeResult> {
  console.log('[NotifService] Subscribe attempt', retryCount + 1, 'for user:', userId);

  // Validate environment
  if (!isPushSupported()) {
    console.warn('[NotifService] Push not supported');
    return { success: false, error: 'Push notifications not supported', errorCode: 'NOT_SUPPORTED' };
  }

  if (isIOS()) {
    if (!isIOSSupportsPush()) {
      console.warn('[NotifService] iOS version too old');
      return { success: false, error: 'iOS 16.4+ required', errorCode: 'IOS_OLD_VERSION' };
    }
    if (!isInstalledPWA()) {
      console.warn('[NotifService] iOS requires PWA installation');
      return { success: false, error: 'App must be installed on home screen', errorCode: 'IOS_NOT_PWA' };
    }
  }

  try {
    // Get or create native subscription
    let subscription = await getNativeSubscription();
    
    if (!subscription) {
      subscription = await createNativeSubscription();
    }

    if (!subscription) {
      if (retryCount < MAX_RETRIES) {
        console.log('[NotifService] Retrying in', RETRY_DELAY, 'ms...');
        await delay(RETRY_DELAY);
        return subscribe(userId, retryCount + 1);
      }
      return { success: false, error: 'Could not create subscription', errorCode: 'SUBSCRIPTION_FAILED' };
    }

    // Save to database for server-side push
    const saved = await saveSubscriptionToDb(userId, subscription);
    
    if (!saved) {
      if (retryCount < MAX_RETRIES) {
        console.log('[NotifService] DB save failed, retrying...');
        await delay(RETRY_DELAY);
        return subscribe(userId, retryCount + 1);
      }
      return { success: false, error: 'Could not save subscription', errorCode: 'DB_SAVE_FAILED' };
    }

    // Update profile
    await supabase
      .from('profiles')
      .update({ push_notifications_enabled: true })
      .eq('id', userId);

    console.log('[NotifService] ✅ Subscription complete');
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[NotifService] Subscribe error:', errorMessage);
    
    // Parse error code from message
    let errorCode: SubscribeResult['errorCode'] = 'SUBSCRIPTION_FAILED';
    if (errorMessage.startsWith('SERVICE_WORKER_NOT_READY')) errorCode = 'SERVICE_WORKER_NOT_READY';
    else if (errorMessage.startsWith('PUSH_MANAGER_NOT_AVAILABLE')) errorCode = 'PUSH_MANAGER_NOT_AVAILABLE';
    else if (errorMessage.startsWith('PERMISSION_DENIED')) errorCode = 'PERMISSION_DENIED';
    else if (errorMessage.startsWith('NETWORK_ERROR')) errorCode = 'NETWORK_ERROR';
    
    // Only retry for network errors
    if (errorCode === 'NETWORK_ERROR' && retryCount < MAX_RETRIES) {
      await delay(RETRY_DELAY);
      return subscribe(userId, retryCount + 1);
    }
    
    return { success: false, error: errorMessage, errorCode };
  }
}

/**
 * Unsubscribe user from push notifications (current device only)
 */
export async function unsubscribe(userId: string): Promise<boolean> {
  console.log('[NotifService] Unsubscribing current device for user:', userId);

  try {
    // Get current device subscription
    const subscription = await getNativeSubscription();
    const currentEndpoint = subscription?.endpoint;
    
    // Unsubscribe from native push
    if (subscription) {
      await subscription.unsubscribe();
      console.log('[NotifService] Native subscription removed');
    }

    // Remove only this device's subscription from database
    await removeSubscriptionFromDb(userId, currentEndpoint);

    // Check if user still has other device subscriptions
    const { data: remainingSubscriptions } = await supabase
      .from('push_subscriptions')
      .select('id')
      .eq('user_id', userId);

    // Only disable in profile if no subscriptions remain
    if (!remainingSubscriptions || remainingSubscriptions.length === 0) {
      console.log('[NotifService] No remaining subscriptions, disabling in profile');
      await supabase
        .from('profiles')
        .update({ push_notifications_enabled: false })
        .eq('id', userId);
    } else {
      console.log('[NotifService] User still has', remainingSubscriptions.length, 'other device(s) subscribed');
    }

    console.log('[NotifService] ✅ Unsubscribe complete');
    return true;
  } catch (error) {
    console.error('[NotifService] Unsubscribe error:', error);
    return false;
  }
}

/**
 * Send a push notification to a user (via Edge Function)
 * The Edge Function will try OneSignal first, then fall back to VAPID
 */
export async function sendNotification(
  userId: string,
  title: string,
  body: string,
  data?: Record<string, unknown>
): Promise<NotificationResult> {
  console.log('[NotifService] Sending notification to:', userId);

  try {
    const { data: result, error } = await supabase.functions.invoke('send-push-notification', {
      body: { userId, title, body, data }
    });

    if (error) {
      console.error('[NotifService] Edge function error:', error);
      return { success: false, method: 'none', error: error.message };
    }

    console.log('[NotifService] Notification result:', result);
    return {
      success: result?.success || false,
      method: result?.method || 'native',
      error: result?.error
    };
  } catch (error) {
    console.error('[NotifService] Send error:', error);
    return { success: false, method: 'none', error: String(error) };
  }
}

/**
 * Show a local notification (immediate, no server)
 */
export async function showLocalNotification(
  title: string,
  body: string,
  options?: { tag?: string; data?: Record<string, unknown> }
): Promise<boolean> {
  if (Notification.permission !== 'granted') {
    console.warn('[NotifService] Permission not granted');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification(title, {
      body,
      icon: '/pwa-192x192.png',
      badge: '/pwa-192x192.png',
      tag: options?.tag || `local-${Date.now()}`,
      data: options?.data,
    });
    
    return true;
  } catch (error) {
    // Fallback to Notification API
    try {
      new Notification(title, { body, icon: '/pwa-192x192.png' });
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Get service worker state
 */
async function getServiceWorkerState(): Promise<NotificationStatus['serviceWorkerState']> {
  if (!('serviceWorker' in navigator)) return 'none';
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return 'none';
    if (registration.active) return 'active';
    if (registration.installing) return 'installing';
    if (registration.waiting) return 'waiting';
    return 'none';
  } catch {
    return 'error';
  }
}

/**
 * Get full notification status
 */
export async function getStatus(userId?: string): Promise<NotificationStatus> {
  const supported = isPushSupported();
  const permission = getPermission();
  const ios = isIOS();
  const iosSupportsPush = isIOSSupportsPush();
  const installedPWA = isInstalledPWA();
  const serviceWorkerState = await getServiceWorkerState();

  let hasNativeSubscription = false;
  let hasDbSubscription = false;
  let subscriptionAge: number | null = null;

  if (supported) {
    const subscription = await getNativeSubscription();
    hasNativeSubscription = !!subscription;
  }

  if (userId) {
    // Check if user has at least one subscription (any device)
    const { data } = await supabase
      .from('push_subscriptions')
      .select('id, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1);
    hasDbSubscription = !!(data && data.length > 0);
    
    if (data && data.length > 0 && data[0].updated_at) {
      const updatedAt = new Date(data[0].updated_at);
      subscriptionAge = Math.floor((Date.now() - updatedAt.getTime()) / (1000 * 60 * 60 * 24)); // days
    }
  }

  const isFullyEnabled = permission === 'granted' && hasDbSubscription;

  return {
    isSupported: supported,
    permission,
    isIOS: ios,
    isIOSSupportsPush: iosSupportsPush,
    isInstalledPWA: installedPWA,
    hasNativeSubscription,
    hasDbSubscription,
    isFullyEnabled,
    subscriptionAge,
    serviceWorkerState
  };
}

/**
 * Auto-sync: If browser has subscription but DB doesn't, sync it
 */
export async function autoSync(userId: string): Promise<boolean> {
  const status = await getStatus(userId);
  
  if (status.hasNativeSubscription && !status.hasDbSubscription) {
    console.log('[NotifService] Auto-syncing subscription to DB...');
    const result = await subscribe(userId);
    return result.success;
  }
  
  return false;
}

/**
 * Force resubscribe: Delete existing subscription and create a new one
 * Useful when notifications stopped working (expired subscription)
 */
export async function resubscribe(userId: string): Promise<boolean> {
  console.log('[NotifService] Force resubscribing user:', userId);

  try {
    // First, unsubscribe from current device
    const subscription = await getNativeSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      console.log('[NotifService] Existing native subscription removed');
    }

    // Remove from DB (current device endpoint)
    await removeSubscriptionFromDb(userId, subscription?.endpoint);

    // Small delay to ensure cleanup is complete
    await delay(500);

    // Create fresh subscription
    const newSubscription = await createNativeSubscription();
    if (!newSubscription) {
      console.error('[NotifService] Failed to create new subscription');
      return false;
    }

    // Save to DB
    const saved = await saveSubscriptionToDb(userId, newSubscription);
    if (!saved) {
      console.error('[NotifService] Failed to save new subscription');
      return false;
    }

    // Update profile
    await supabase
      .from('profiles')
      .update({ push_notifications_enabled: true })
      .eq('id', userId);

    console.log('[NotifService] ✅ Resubscribe complete');
    return true;
  } catch (error) {
    console.error('[NotifService] Resubscribe error:', error);
    return false;
  }
}

/**
 * Listen for subscription changes from service worker
 */
export function listenForChanges(callback: () => void): () => void {
  const handler = (event: MessageEvent) => {
    if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
      console.log('[NotifService] Subscription changed event');
      callback();
    }
  };
  
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handler);
  }
  
  return () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', handler);
    }
  };
}
